import { styled } from "goober";
import {
  useLayoutEffect,
  useRef,
  type ComponentProps,
  type ReactNode,
} from "react";
import { EASE_OUT, MOTION_BASE_MS } from "./motion";

const Container = styled("div")`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  min-height: 0;
  &:has(> [data-page-surface]) {
    anchor-name: --toast-page-body;
  }
`;

export function PageSurface(props: ComponentProps<"div">) {
  return <div data-page-surface {...props} />;
}

export function PageBody({
  enter,
  children,
}: {
  enter?: "left" | "right";
  children: ReactNode;
}) {
  const bodyRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const body = bodyRef.current;
    if (
      !body ||
      !enter ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }

    const animations: Animation[] = [];
    const start = () => {
      const contents = body.querySelectorAll<HTMLElement>(
        "[data-page-surface] > *"
      );
      if (!contents.length) return;
      observer.disconnect();
      for (const content of contents) {
        animations.push(
          content.animate(
            [
              {
                transform: `translateX(${enter === "right" ? 4 : -4}px)`,
                opacity: 0.92,
              },
              { transform: "none", opacity: 1 },
            ],
            { duration: MOTION_BASE_MS, easing: EASE_OUT }
          )
        );
      }
    };
    const observer = new MutationObserver(start);
    observer.observe(body, { childList: true, subtree: true });
    start();

    return () => {
      observer.disconnect();
      animations.forEach((animation) => animation.cancel());
    };
  }, [enter]);

  return (
    <Container ref={bodyRef} data-page-body>
      {children}
    </Container>
  );
}
